import { TRAINING_ZONE_COLORS_BY_NUMBER, trainingZoneColor, defaultPowerZoneForPercent, DEFAULT_TRAINING_ZONE_NUMBERS } from './trainingZones';
/**
 * Convert between API workout structure (mobile format) and WorkoutBuilder internal steps.
 */

export interface ApiStructTarget {
  minValue?: number | string;
  maxValue?: number | string;
  type?: string;
  unit?: string;
}

export interface ApiStructStep {
  /** Shape is independent of the allowed target intensity range. */
  isRamp?: boolean;
  name?: string;
  description?: string;
  intensityClass?: string;
  intensity?: string;
  length?: { value: number | string; unit?: string };
  type?: string;
  openDuration?: string;
  targets?: ApiStructTarget[];
}

export interface ApiStructBlock {
  type?: string;
  length?: { value: number | string; unit?: string };
  steps?: ApiStructStep[];
  begin?: string | number;
  end?: string | number;
}

export interface ApiWorkoutStructure {
  structure: ApiStructBlock[];
  primaryLengthMetric?: string;
  primaryIntensityMetric?: string;
  primaryIntensityTargetOrRange?: string;
}

/** Manual lap (Garmin OPEN) vs fixed duration — string "false" is NOT open. */
export function isOpenDurationStep(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export function openDurationStorageValue(isOpen: boolean): string {
  return isOpen ? 'true' : 'false';
}

export interface BuilderSimpleStep {
  id: number;
  type: 'simple';
  name: string;
  intensityClass?: string;
  duration: number;
  min: number;
  max: number;
  zone: number;
  isRamp: boolean;
}

export interface BuilderRepeatBlock {
  id: number;
  type: 'repeat';
  name?: string;
  count: number;
  steps: BuilderSimpleStep[];
}

export type BuilderWorkoutStep = BuilderSimpleStep | BuilderRepeatBlock;

/** Legacy heuristic: values above this on %FTP targets were stored as absolute watts. */
export const ABSOLUTE_POWER_THRESHOLD = 150;
/** Builder wheel + step editor — % FTP index 0…1000. */
export const FTP_PERCENT_MAX = 1000;
export const HR_PERCENT_MAX = 150;

export interface StructureParseOptions {
  ftp?: number;
  ltHr?: number;
  thresholdSpeedMps?: number;
  primaryIntensityMetric?: string;
}

function storedTargetsLookLikeAbsolute(
  min: number,
  max: number,
  metric: string,
): boolean {
  const mid = (min + max) / 2;
  if (metric === 'percentOfFtp' || !metric) {
    return mid > ABSOLUTE_POWER_THRESHOLD || max > ABSOLUTE_POWER_THRESHOLD;
  }
  if (metric === 'percentOfLTHR' || metric === 'percentOfMaxHr') {
    return mid > HR_PERCENT_MAX || max > HR_PERCENT_MAX;
  }
  return false;
}

/** Convert legacy absolute watts/BPM stored in DB → percent indices for builder state. */
export function normalizeStoredTargetToPercent(
  minRaw: number | string | undefined,
  maxRaw: number | string | undefined,
  metric: string,
  options: StructureParseOptions,
): { min: number; max: number } {
  let min = Number(minRaw) || 0;
  let max = Number(maxRaw) || min;
  if (max < min) [min, max] = [max, min];

  if (!storedTargetsLookLikeAbsolute(min, max, metric)) {
    return { min, max };
  }

  const ftp = options.ftp ?? 0;
  const ltHr = options.ltHr ?? 0;

  if ((metric === 'percentOfFtp' || !metric) && ftp > 0) {
    return {
      min: Math.round((min / ftp) * 100),
      max: Math.round((max / ftp) * 100),
    };
  }
  if ((metric === 'percentOfLTHR' || metric === 'percentOfMaxHr') && ltHr > 0) {
    return {
      min: Math.round((min / ltHr) * 100),
      max: Math.round((max / ltHr) * 100),
    };
  }

  return { min: Math.round(min), max: Math.round(max) };
}

/** UI label — percent with optional absolute watts/BPM in parentheses. */
export function formatIntensityTargetLabel(
  minRaw: number | string | undefined,
  maxRaw: number | string | undefined,
  metric: string,
  options: StructureParseOptions,
): string {
  const { min, max } = normalizeStoredTargetToPercent(minRaw, maxRaw, metric, options);
  const low = Math.min(min, max);
  const high = Math.max(min, max);
  const unit =
    metric === 'percentOfMaxHr'
      ? '% Max HR'
      : metric === 'percentOfLTHR'
        ? '% LTHR'
        : metric === 'percentOfThresholdSpeed'
          ? '% PACE'
          : '% FTP';
  const base = `${Number(low.toFixed(2))}–${Number(high.toFixed(2))} ${unit}`;

  const ftp = options.ftp ?? 0;
  const ltHr = options.ltHr ?? 0;

  if ((metric === 'percentOfFtp' || !metric) && ftp > 0) {
    const wMin = Math.round((low / 100) * ftp);
    const wMax = Math.round((high / 100) * ftp);
    return `${base} · ${wMin}–${wMax} W`;
  }
  if ((metric === 'percentOfLTHR' || metric === 'percentOfMaxHr') && ltHr > 0) {
    const bMin = Math.round((low / 100) * ltHr);
    const bMax = Math.round((high / 100) * ltHr);
    return `${base} · ${bMin}–${bMax} bpm`;
  }

  const thr = options.thresholdSpeedMps ?? 0;
  if (metric === 'percentOfThresholdSpeed' && thr > 0) {
    const fmt = (speed: number) => {
      const sec = Math.round(1000 / speed);
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };
    const paceFast = fmt((high / 100) * thr);
    const paceSlow = fmt((low / 100) * thr);
    return `${base} · ${paceFast}–${paceSlow} /km`;
  }

  return base;
}

/** Workout detail step list — absolute watts/BPM/pace for athletes; % kept in API/watch sync only. */
export function formatStructureStepTargetDisplay(
  minRaw: number | string | undefined,
  maxRaw: number | string | undefined,
  metric: string,
  options: StructureParseOptions & { maxHr?: number },
): string {
  const resolvedMetric = metric || 'percentOfFtp';
  const minNum = Number(minRaw);
  const maxNum = Number(maxRaw);

  if (
    minRaw != null &&
    maxRaw != null &&
    (resolvedMetric === 'percentOfFtp' || !metric) &&
    storedTargetsLookLikeAbsolute(minNum, maxNum, resolvedMetric)
  ) {
    const low = Math.min(minNum, maxNum);
    const high = Math.max(minNum, maxNum);
    return `${Math.round(low)}–${Math.round(high)} W`;
  }

  const { min, max } = normalizeStoredTargetToPercent(minRaw, maxRaw, resolvedMetric, options);
  const low = Math.min(min, max);
  const high = Math.max(min, max);

  const ftp = options.ftp ?? 0;
  const ltHr = options.ltHr ?? 0;
  const maxHr = options.maxHr ?? 0;
  const thr = options.thresholdSpeedMps ?? 0;

  if ((resolvedMetric === 'percentOfFtp' || !metric) && ftp > 0) {
    return `${Math.round((low / 100) * ftp)}–${Math.round((high / 100) * ftp)} W`;
  }
  if (resolvedMetric === 'percentOfLTHR' && ltHr > 0) {
    return `${Math.round((low / 100) * ltHr)}–${Math.round((high / 100) * ltHr)} bpm`;
  }
  if (resolvedMetric === 'percentOfMaxHr' && maxHr > 0) {
    return `${Math.round((low / 100) * maxHr)}–${Math.round((high / 100) * maxHr)} bpm`;
  }
  if (resolvedMetric === 'percentOfThresholdSpeed' && thr > 0) {
    const fmtPace = (speedMps: number) => {
      const sec = Math.round(1000 / speedMps);
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };
    return `${fmtPace((high / 100) * thr)}–${fmtPace((low / 100) * thr)} /km`;
  }

  return formatIntensityTargetLabel(minRaw, maxRaw, resolvedMetric, options);
}

/** Parse API step length into seconds (handles min/sec units and legacy payloads). */
export function stepLengthSecs(length?: { value: number | string; unit?: string }): number {
  if (!length) return 0;
  const v = Number(length.value) || 0;
  const u = (length.unit ?? '').toLowerCase();
  if (u === 'repetition' || u === 'rep' || u === 'reps') return 0;
  if (u === 'minute' || u === 'minutes' || u === 'min') return v * 60;
  if (u === 'hour' || u === 'hours' || u === 'hr') return v * 3600;
  if (u === 'second' || u === 'seconds' || u === 'sec' || u === 's') return v;
  // No unit — small whole numbers are often minutes in legacy payloads
  if (!u && v > 0 && v <= 60 && Number.isInteger(v)) return v * 60;
  return v;
}

/** Resolve FTP % range from targets or intensity class fallback. */
export function stepTargetRange(
  step: ApiStructStep,
  options?: StructureParseOptions,
): { min: number; max: number } {
  const metric = options?.primaryIntensityMetric ?? 'percentOfFtp';
  const t = step.targets?.[0];
  if (t && (t.minValue != null || t.maxValue != null)) {
    return normalizeStoredTargetToPercent(t.minValue, t.maxValue, metric, options ?? {});
  }
  const ic = (step.intensityClass ?? step.intensity ?? '').toLowerCase();
  const fallback: Record<string, [number, number]> = {
    warmup: [45, 55], active: [90, 100], rest: [40, 50],
    recovery: [40, 50], cooldown: [40, 50], interval: [95, 105], race: [105, 120],
  };
  const [min, max] = fallback[ic] ?? [50, 60];
  return { min, max };
}

export function zoneForPct(pct: number): number {
  return defaultPowerZoneForPercent(pct);
}

/** Render slopes only for explicit ramps; target ranges are steady by default. */
export function isRampStep(step: ApiStructStep): boolean {
  if (typeof step.isRamp === 'boolean') return step.isRamp;
  // Older explicit ramp blocks used their type or name instead of a shape flag.
  // Warmup/cooldown and a wide target range alone describe purpose/tolerance.
  return (step.type ?? '').toLowerCase() === 'ramp' ||
    /\bramp(?:\s+(?:up|down))?\b/i.test(step.name ?? step.description ?? '');
}

/**
 * Left → right intensity endpoints for ramp polygons (analysis overlay).
 * Chart bars use {@link rampBarHeightPct} for stepped rects instead.
 */
export function rampSlopeEndpoints(
  step: ApiStructStep,
  options?: StructureParseOptions,
): { startPct: number; endPct: number } {
  const { min, max } = stepTargetRange(step, options);
  const low = Math.min(min, max);
  const high = Math.max(min, max);
  const name = (step.name ?? step.description ?? '').toLowerCase();
  const ic = (step.intensityClass ?? step.intensity ?? '').toLowerCase();

  if (ic === 'cooldown' || name.includes('ramp down')) {
    return { startPct: high, endPct: low };
  }
  return { startPct: low, endPct: high };
}

/**
 * Stepped chart bar height for ramp / warmup / cooldown segments.
 * Always uses the high end of the target range so equal ranges render equal bar heights
 * (e.g. Ramp Up 3 and Ramp Down 1 both at 75–85 % → same bar).
 */
export function rampBarHeightPct(
  step: ApiStructStep,
  options?: StructureParseOptions,
): number {
  const { min, max } = stepTargetRange(step, options);
  return Math.max(min, max);
}

/** Flat preview bars show mean target intensity, including ranges and ramps.
 * A wide target range is not a sustained effort at its upper bound.
 */
export function structureBarHeightPct(
  step: ApiStructStep,
  options?: StructureParseOptions,
): number {
  const { min, max } = stepTargetRange(step, options);
  return (min + max) / 2;
}

function apiStepToBuilder(
  step: ApiStructStep,
  id: number,
  options?: StructureParseOptions,
): BuilderSimpleStep {
  const { min, max } = stepTargetRange(step, options);
  const mid = (min + max) / 2;
  return {
    id,
    type: 'simple',
    name: step.name ?? step.description ?? 'Step',
    intensityClass: step.intensityClass ?? step.intensity,
    duration: stepLengthSecs(step.length),
    min,
    max,
    zone: zoneForPct(mid),
    isRamp: isRampStep(step),
  };
}

/**
 * Flat timeline for mini bar charts (calendar, library) — expands repetition/repeat blocks.
 */
export function collapseStructurePreview(blocks: ApiStructBlock[]): ApiStructStep[] {
  const out: ApiStructStep[] = [];
  blocks.forEach(block => {
    const steps = block.steps ?? [];
    if (!steps.length) {
      out.push(block as unknown as ApiStructStep);
      return;
    }
    const blockType = (block.type ?? '').toLowerCase();
    const repeatCount =
      (blockType === 'repetition' || blockType === 'repeat')
        ? Math.max(1, Number(block.length?.value) || 1)
        : 1;
    for (let r = 0; r < repeatCount; r++) {
      steps.forEach(step => out.push(step));
    }
  });
  return out;
}

/** Display groups for workout detail step lists — repeats collapsed to one template. */
export interface StructureDisplayGroup {
  kind: 'step' | 'repeat';
  steps: ApiStructStep[];
  repeatCount: number;
}

export function groupStructureBlocksForDisplay(blocks: ApiStructBlock[]): StructureDisplayGroup[] {
  return blocks.map(block => {
    const steps = block.steps ?? [];
    const blockType = (block.type ?? '').toLowerCase();
    const isRepeatBlock =
      (blockType === 'repetition' || blockType === 'repeat') &&
      (Number(block.length?.value) || 1) > 1;

    if (isRepeatBlock && steps.length > 0) {
      return {
        kind: 'repeat',
        steps,
        repeatCount: Number(block.length?.value) || 1,
      };
    }
    if (steps.length === 1) {
      return { kind: 'step', steps: [steps[0]], repeatCount: 1 };
    }
    if (steps.length > 1) {
      return { kind: 'repeat', steps, repeatCount: 1 };
    }
    return { kind: 'step', steps: [block as unknown as ApiStructStep], repeatCount: 1 };
  });
}

/** Normalize planned duration — API stores seconds; legacy quick-add used minutes. */
export function plannedTimeToSeconds(val: number, hasStructure: boolean): number {
  if (hasStructure) return val;
  if (val > 0 && val < 500) return val * 60;
  return val;
}

/** Compact h/m display for library cards (input: seconds). */
export function formatPlannedDuration(secs: number | null | undefined, hasStructure = false): string {
  if (secs == null || !Number(secs)) return '—';
  const total = Math.round(plannedTimeToSeconds(Number(secs), hasStructure));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${total}s`;
}

/** Distance in meters → km string for library cards. */
export function formatPlannedDistance(meters: number | null | undefined): string {
  if (meters == null || !Number(meters)) return '—';
  const km = Number(meters) / 1000;
  if (km >= 100) return String(Math.round(km));
  if (km >= 10) return km.toFixed(1);
  return km.toFixed(2);
}

/** API structure → WorkoutBuilder steps. */
export function apiStructureToBuilderSteps(
  structure: ApiWorkoutStructure | null | undefined,
  parseOptions?: StructureParseOptions,
): BuilderWorkoutStep[] {
  const blocks = structure?.structure ?? [];
  if (!blocks.length) return [];

  const options: StructureParseOptions = {
    ftp: parseOptions?.ftp,
    ltHr: parseOptions?.ltHr,
    primaryIntensityMetric:
      parseOptions?.primaryIntensityMetric ??
      structure?.primaryIntensityMetric ??
      'percentOfFtp',
  };

  let id = 1;
  const out: BuilderWorkoutStep[] = [];

  blocks.forEach(block => {
    const steps = block.steps ?? [];
    const reps = block.type === 'repetition' ? (Number(block.length?.value) || 1) : 1;

    if (block.type === 'repetition' && steps.length > 0) {
      out.push({
        id: id++,
        type: 'repeat',
        count: reps,
        steps: steps.map(s => apiStepToBuilder(s, id++, options)),
      });
      return;
    }

    if (steps.length === 1) {
      out.push(apiStepToBuilder(steps[0], id++, options));
      return;
    }

    if (steps.length > 1) {
      out.push({
        id: id++,
        type: 'repeat',
        count: 1,
        steps: steps.map(s => apiStepToBuilder(s, id++, options)),
      });
      return;
    }

    out.push(apiStepToBuilder(block as unknown as ApiStructStep, id++, options));
  });

  return out;
}

function intensityClassFromStep(step: BuilderSimpleStep): string {
  if (step.intensityClass) return step.intensityClass;
  const ic = step.name.toLowerCase();
  if (ic.includes('warm')) return 'warmUp';
  if (ic.includes('cool')) return 'coolDown';
  if (step.zone <= 1 || ic.includes('rest') || ic.includes('recovery')) return 'rest';
  if (step.zone >= 4) return 'active';
  return 'active';
}

function builderStepToApi(
  step: BuilderSimpleStep,
  intensityMetric = 'percentOfFtp',
  lengthMetric: 'duration' | 'distance' = 'duration',
): ApiStructStep {
  const lengthUnit = lengthMetric === 'distance' ? 'meter' : 'second';
  return {
    name: step.name,
    isRamp: step.isRamp,
    type: 'step',
    length: { value: String(step.duration), unit: lengthUnit },
    targets: [{
      minValue: String(step.min),
      maxValue: String(step.max),
      type: intensityMetric,
    }],
    intensityClass: intensityClassFromStep(step),
    openDuration: 'false',
  };
}

/** Compatibility alias for the shared zone roles. */
export const STRUCTURE_ZONE_COLORS = TRAINING_ZONE_COLORS_BY_NUMBER;

export const WORKOUT_ZONE_NUMBERS = DEFAULT_TRAINING_ZONE_NUMBERS;

/** Map FTP % midpoint → zone colour (Z1–Z7). */
export function zoneColorForPct(pct: number): string {
  return trainingZoneColor(zoneForPct(pct));
}

export function stepZoneNumber(
  step: ApiStructStep,
  options?: StructureParseOptions,
): number {
  const { min, max } = stepTargetRange(step, options);
  return zoneForPct((min + max) / 2);
}

/** Block colour from FTP % zone — shared across builder, calendar, detail bars. */
export function stepZoneColor(
  step: ApiStructStep,
  options?: StructureParseOptions,
): string {
  const { min, max } = stepTargetRange(step, options);
  return zoneColorForPct((min + max) / 2);
}

/** Flattened timeline segment for chart overlays (elapsed seconds from workout start). */
export interface StructureTimelineSegment {
  index: number;
  name: string;
  startSec: number;
  endSec: number;
  durationSec: number;
  minPct: number;
  maxPct: number;
  zone: number;
  isRamp: boolean;
}

export type StructureCompliance = 'on_target' | 'under' | 'over' | 'unknown';

/** Flatten builder steps (expand repeats) into ordered timeline segments. */
export function flattenBuilderSteps(steps: BuilderWorkoutStep[]): BuilderSimpleStep[] {
  const out: BuilderSimpleStep[] = [];
  steps.forEach(step => {
    if (step.type === 'repeat') {
      for (let i = 0; i < step.count; i++) step.steps.forEach(s => out.push(s));
    } else {
      out.push(step);
    }
  });
  return out;
}

function builderSimpleToSegment(step: BuilderSimpleStep, index: number, startSec: number): StructureTimelineSegment {
  return {
    index,
    name: step.name,
    startSec,
    endSec: startSec + step.duration,
    durationSec: step.duration,
    minPct: step.min,
    maxPct: step.max,
    zone: step.zone,
    isRamp: step.isRamp,
  };
}

function apiStepToSegment(step: ApiStructStep, index: number, startSec: number, durationSec: number): StructureTimelineSegment {
  const { min, max } = stepTargetRange(step);
  const mid = (min + max) / 2;
  return {
    index,
    name: step.name ?? step.description ?? 'Step',
    startSec,
    endSec: startSec + durationSec,
    durationSec,
    minPct: min,
    maxPct: max,
    zone: zoneForPct(mid),
    isRamp: isRampStep(step),
  };
}

/** Build timeline from API block begin/end when present (authoritative offsets). */
function buildStructureTimelineFromOffsets(blocks: ApiStructBlock[]): StructureTimelineSegment[] | null {
  let index = 0;
  const out: StructureTimelineSegment[] = [];
  let sawOffsets = false;

  for (const block of blocks) {
    const steps = block.steps ?? [];
    const reps = block.type === 'repetition' ? (Number(block.length?.value) || 1) : 1;
    const blockBegin = Number(block.begin);
    const blockEnd = Number(block.end);
    const hasRange = Number.isFinite(blockBegin) && Number.isFinite(blockEnd) && blockEnd > blockBegin;

    if (!steps.length) {
      if (!hasRange) continue;
      sawOffsets = true;
      const dur = blockEnd - blockBegin;
      out.push(apiStepToSegment(block as unknown as ApiStructStep, index++, blockBegin, dur));
      continue;
    }

    const templateDur = steps.reduce((sum, step) => sum + stepLengthSecs(step.length), 0);
    const blockSpan = hasRange ? blockEnd - blockBegin : templateDur * reps;
    const durScale = templateDur > 0 ? blockSpan / (templateDur * reps) : 1;

    for (let r = 0; r < reps; r++) {
      let cursor = hasRange
        ? blockBegin + r * (blockSpan / reps)
        : (out.length ? out[out.length - 1].endSec : 0);

      for (const step of steps) {
        const rawDur = stepLengthSecs(step.length);
        const dur = rawDur * durScale;
        if (hasRange) sawOffsets = true;

        out.push(apiStepToSegment(step, index++, cursor, dur));
        cursor += dur;
      }
    }
  }

  return sawOffsets ? out : null;
}

/** Scale segment timeline so the plan spans exactly 0 … plannedTotalSec. */
export function normalizeStructureToPlannedDuration(
  segments: StructureTimelineSegment[],
  plannedTotalSec: number,
): StructureTimelineSegment[] {
  if (!segments.length || plannedTotalSec <= 0) return segments;
  const structTotal = segments[segments.length - 1].endSec;
  if (structTotal <= 0) return segments;
  const scale = plannedTotalSec / structTotal;
  if (Math.abs(scale - 1) < 0.005) return segments;
  return segments.map(seg => ({
    ...seg,
    startSec: seg.startSec * scale,
    endSec: seg.endSec * scale,
    durationSec: seg.durationSec * scale,
  }));
}

/** API structure → timeline segments; anchored to total_time_planned when provided. */
export function buildStructureTimeline(
  structure: ApiWorkoutStructure | null | undefined,
  plannedTotalSec?: number | null,
): StructureTimelineSegment[] {
  const blocks = structure?.structure ?? [];
  if (!blocks.length) return [];

  const fromOffsets = buildStructureTimelineFromOffsets(blocks);
  let segments: StructureTimelineSegment[];

  if (fromOffsets?.length) {
    segments = fromOffsets;
  } else {
    const flat = flattenBuilderSteps(apiStructureToBuilderSteps(structure));
    let offset = 0;
    segments = flat.map((step, idx) => {
      const seg = builderSimpleToSegment(step, idx, offset);
      offset += step.duration;
      return seg;
    });
  }

  if (plannedTotalSec != null && plannedTotalSec > 0) {
    segments = normalizeStructureToPlannedDuration(segments, plannedTotalSec);
  }

  return segments;
}

/** Chart X-axis = completed workout duration (seconds from start), never time-of-day. */
export function resolveChartTimeBase(actualTotalSec: number): number {
  return Math.max(actualTotalSec, 1);
}

export function secToChartFrac(timeSec: number, timeBase: number): number {
  if (timeBase <= 0) return 0;
  return Math.max(0, Math.min(1, timeSec / timeBase));
}

export function streamIndexToChartFrac(
  index: number,
  stream: Array<{ timeSeconds: number }>,
  timeBase: number,
): number {
  if (!stream.length) return 0;
  const t = stream[index]?.timeSeconds;
  if (t != null && isFinite(t)) return secToChartFrac(t, timeBase);
  return stream.length > 1 ? index / (stream.length - 1) : 0;
}

export interface StreamTimelinePoint {
  timeSeconds: number;
  timestamp: number | null;
}

/**
 * Force stream elapsed times to 0 … actualDurationSec (workout duration).
 * Fixes: missing elapsed_time (i×5 fallback), time-of-day, Unix timestamps.
 */
export function normalizeWorkoutStreamTimeline<T extends StreamTimelinePoint>(
  points: T[],
  actualDurationSec: number,
): T[] {
  if (!points.length || actualDurationSec <= 0) return points;

  const n = points.length;
  let times = points.map(p => p.timeSeconds);
  const first = times[0];
  const last = times[n - 1];
  let span = last - first;

  const ts0 = points[0].timestamp;
  const tsLast = points[n - 1].timestamp;
  const hasUnixTs = ts0 != null && tsLast != null && ts0 > 1e9 && tsLast > ts0;

  // Raw values are Unix timestamps (in timeSeconds or timestamp field)
  if (first > 1e9) {
    times = times.map(t => t - first);
    span = times[n - 1];
  } else if (hasUnixTs) {
    const spanTs = tsLast! - ts0!;
    const tsMismatch = span <= 0
      || span > actualDurationSec * 1.4
      || span < actualDurationSec * 0.55
      || Math.abs(span - spanTs) > actualDurationSec * 0.2;
    if (tsMismatch) {
      times = points.map(p => ((p.timestamp ?? ts0!) - ts0!));
      span = times[n - 1];
    }
  }

  // Seconds since midnight (time-of-day) — e.g. 09:00 → 32400
  if (first >= 3600 && first < 86400 && span > 0 && span <= actualDurationSec * 1.5) {
    times = times.map(t => t - first);
    span = times[n - 1];
  }

  // Detect i×5 synthetic fallback when elapsed_time is missing from API
  const fallbackEnd = (n - 1) * 5;
  const isIndexFallback = n > 10
    && Math.abs(last - fallbackEnd) <= Math.max(10, n * 0.01)
    && first < 120;

  const spanMismatch = span <= 0
    || span > actualDurationSec * 1.35
    || span < actualDurationSec * 0.55;

  if (isIndexFallback || spanMismatch) {
    times = n > 1
      ? points.map((_, i) => (i / (n - 1)) * actualDurationSec)
      : [0];
    span = actualDurationSec;
  } else if (first > 0.5 && first < 3600) {
    times = times.map(t => t - first);
    span = times[n - 1] - times[0];
  }

  // Align end to authoritative workout duration (preserves curve shape)
  const finalSpan = times[n - 1] - times[0];
  if (finalSpan > 0 && Math.abs(finalSpan - actualDurationSec) / actualDurationSec > 0.03) {
    const scale = actualDurationSec / finalSpan;
    const base = times[0];
    times = times.map(t => (t - base) * scale);
  }

  return points.map((p, i) => ({ ...p, timeSeconds: Math.max(0, times[i]) }));
}

/** Compare average power in a segment window against planned FTP % range (±10%). */
export function evaluateSegmentCompliance(
  stream: Array<{ timeSeconds: number; power: number | null }>,
  seg: StructureTimelineSegment,
  ftp: number | null,
  toleranceRatio = 0.1,
): StructureCompliance {
  if (!ftp || ftp <= 0) return 'unknown';

  const pts = stream.filter(
    p => p.timeSeconds >= seg.startSec
      && p.timeSeconds < seg.endSec
      && p.power != null
      && isFinite(p.power)
      && p.power > 0,
  );
  if (!pts.length) return 'unknown';

  const avg = pts.reduce((sum, p) => sum + p.power!, 0) / pts.length;
  const targetMin = (ftp * seg.minPct) / 100;
  const targetMax = (ftp * seg.maxPct) / 100;
  const lo = targetMin * (1 - toleranceRatio);
  const hi = targetMax * (1 + toleranceRatio);

  if (avg >= lo && avg <= hi) return 'on_target';
  if (avg > hi) return 'over';
  return 'under';
}

/** Seconds on workout timeline for builder steps (distance steps estimated from threshold speed). */
export function builderStepTimelineSeconds(
  step: BuilderSimpleStep,
  lengthMetric: 'duration' | 'distance',
  thresholdSpeedMps = 0,
): number {
  if (lengthMetric !== 'distance') {
    return step.duration;
  }
  if (thresholdSpeedMps > 0 && step.duration > 0) {
    const fraction = (step.min + step.max) / 2 / 100;
    const speed = thresholdSpeedMps * (fraction > 0 ? fraction : 1);
    return Math.max(1, Math.round(step.duration / speed));
  }
  return 0;
}

/** Walk blocks and normalize step targets before API persistence (always percent). */
export function normalizeWorkoutBlocksTargets(
  blocks: unknown[],
  metric: string,
  thresholds: StructureParseOptions,
  targetOrRange?: string,
): unknown[] {
  if (!Array.isArray(blocks)) return blocks;
  const singleMode = targetOrRange === 'single';

  const normalizeNode = (node: Record<string, unknown>): Record<string, unknown> => {
    const out = { ...node };

    if (Array.isArray(out.targets) && (out.targets as unknown[])[0]) {
      const t = (out.targets as { minValue?: number | string; maxValue?: number | string }[])[0];
      const { min, max } = normalizeStoredTargetToPercent(
        t.minValue,
        t.maxValue,
        metric,
        thresholds,
      );
      const pctMin = singleMode ? Math.round((min + max) / 2) : min;
      const pctMax = singleMode ? pctMin : max;
      out.targets = [
        { ...t, minValue: String(pctMin), maxValue: String(pctMax) },
        ...(out.targets as unknown[]).slice(1),
      ];
    }

    if (Array.isArray(out.steps)) {
      out.steps = (out.steps as Record<string, unknown>[]).map(s => normalizeNode(s));
    }

    return out;
  };

  return (blocks as Record<string, unknown>[]).map(block => normalizeNode(block));
}

/** WorkoutBuilder steps → API structure wrapper. */
export function builderStepsToApiStructure(
  steps: BuilderWorkoutStep[],
  meta?: Partial<ApiWorkoutStructure> & {
    thresholdSpeedMps?: number;
    ftp?: number;
    ltHr?: number;
    maxHr?: number;
  },
): ApiWorkoutStructure {
  const intensityMetric = meta?.primaryIntensityMetric ?? 'percentOfFtp';
  const lengthMetric = meta?.primaryLengthMetric === 'distance' ? 'distance' : 'duration';
  const thresholdSpeedMps = meta?.thresholdSpeedMps ?? 0;
  let offset = 0;
  const structure: ApiStructBlock[] = steps.map(step => {
    if (step.type === 'repeat') {
      const segDur = step.steps.reduce(
        (a, s) => a + builderStepTimelineSeconds(s, lengthMetric, thresholdSpeedMps),
        0,
      );
      const totalDur = segDur * step.count;
      const block: ApiStructBlock = {
        type: 'repetition',
        length: { value: String(step.count), unit: 'repetition' },
        steps: step.steps.map(s => builderStepToApi(s, intensityMetric, lengthMetric)),
        begin: String(offset),
        end: String(offset + totalDur),
      };
      offset += totalDur;
      return block;
    }

    const timelineSec = builderStepTimelineSeconds(step, lengthMetric, thresholdSpeedMps);
    const block: ApiStructBlock = {
      type: 'step',
      length: { value: '1', unit: 'repetition' },
      steps: [builderStepToApi(step, intensityMetric, lengthMetric)],
      begin: String(offset),
      end: String(offset + timelineSec),
    };
    offset += timelineSec;
    return block;
  });

  const thresholdOpts: StructureParseOptions = {
    ftp: meta?.ftp,
    ltHr: meta?.ltHr,
    thresholdSpeedMps,
    primaryIntensityMetric: intensityMetric,
  };

  const normalizedStructure =
    meta?.ftp || meta?.ltHr || meta?.maxHr
      ? (normalizeWorkoutBlocksTargets(
          structure,
          intensityMetric,
          thresholdOpts,
          meta?.primaryIntensityTargetOrRange,
        ) as ApiStructBlock[])
      : structure;

  return {
    primaryLengthMetric: meta?.primaryLengthMetric ?? 'duration',
    primaryIntensityMetric: meta?.primaryIntensityMetric ?? 'percentOfFtp',
    primaryIntensityTargetOrRange: meta?.primaryIntensityTargetOrRange ?? 'range',
    structure: normalizedStructure,
  };
}
